import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { PLAYER_SCHEMA, PLAYER_CLAIM_SCHEMA, MAPPED_PLAYER_SCHEMA } from '../schema/playerSchema';
import { ClaimFilters } from '../components/claim/claimFilters';
import ClaimHistoryTable from '../components/table/claimHistoryTable';
import { Progress } from '../components/util/progress';
import {
  GOLDEN_PLAYER_AND_CLAIMS,
  SAVE_PLAYER_CLAIM,
  OVERRIDE_PLAYER_CLAIM,
  INSERT_GOLDEN_PLAYER,
} from '../hasura/queries/player/playerClaims';
import { VIEW_PLAYER_MAPPINGS, DELETE_PLAYER_MAPPING } from '../hasura/queries/player/playerMapping';
import { SIMILAR_PLAYERS, DELETE_PLAYER } from '../hasura/queries/player/playerList';
import { blankSchema } from '../util/schema';

import { Error } from '../components/util/notifications';

import MappingView from '../components/mapping/mappingView';
import { LinkButton } from '../components/util/buttons';
import { SimilarEntity } from '../components/mapping/SimilarEntity';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenPlayerView = ({ playerId, isNewPlayer, history }) => {
  const historyPath = history.location.search;

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewPlayer ? blankSchema(PLAYER_SCHEMA) : {});
  const [overrides, setOverrides] = useState({});

  const [claimMode, setClaimMode] = useState(isNewPlayer ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);

  const setNewEntityMode = () => {
    setClaimMode(CLAIM_MODE_NEW_ENTITY);
    setClaimStatuses(blankSchema(PLAYER_SCHEMA));
  };

  const setViewMode = () => {
    setClaimMode(CLAIM_MODE_VIEW);
    setClaimStatuses({});
  };

  useEffect(() => (historyPath === '?new=new' ? setNewEntityMode() : setViewMode()), [historyPath]);

  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_PLAYER_AND_CLAIMS, {
    variables: {
      player_id: playerId,
    },
  });

  if (loading) return <Progress />;

  let allClaims = [];
  data &&
    data.statsbomb_player &&
    data.statsbomb_player[0] &&
    data.statsbomb_player[0].mapping_players.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_players];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_player_uuid === claimUUID;
    })[0];
    if (claim) {
      PLAYER_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    PLAYER_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(blankSchema(PLAYER_SCHEMA));
  };

  const getGoldenPlayerId = insertedPlayer => insertedPlayer?.data?.insert_statsbomb_player?.returning?.[0]?.player_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_player_uuid);
  };

  const playerName = data.statsbomb_player[0]?.player_name;

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <Grid container spacing={3} justify="center" alignItems="center">
          <Grid item xs={6}>
            <h2>Player Singular: {isNewPlayer ? ' New Player' : playerId}</h2>
          </Grid>
          <Grid item xs={6}>
            {claimMode !== CLAIM_MODE_NEW_ENTITY && (
              <Link
                to={{
                  pathname: `/transfer-history`,
                  search: `?player_id=${playerId}`,
                }}
                component={LinkButton}
                label="View Transfer History"
              />
            )}
          </Grid>
        </Grid>
        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="player"
              schema={PLAYER_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{ headerValue: 'Player Name', bodyValue: playerName }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}
        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <MappingView
            type="player"
            recordId={playerId}
            schema={MAPPED_PLAYER_SCHEMA}
            subscription={VIEW_PLAYER_MAPPINGS}
            deleteMutation={DELETE_PLAYER_MAPPING}
            deleteEntityMutation={DELETE_PLAYER}
            entityKey="provider_player_id"
            styles={{ tableContatinerClasses, buttonClasses }}
          />
        )}
        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Player Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_player[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_player_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={PLAYER_SCHEMA}
            saveMutation={SAVE_PLAYER_CLAIM}
            overrideMutation={OVERRIDE_PLAYER_CLAIM}
            createMutation={INSERT_GOLDEN_PLAYER}
            sourcePK="provider_player_id"
            claimPK="claim_player_uuid"
            goldenEntityPK="player_id"
            getGoldenEntityId={getGoldenPlayerId}
            entityType="player"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            similarEntityTable={SimilarEntity}
            similarEntityQuery={SIMILAR_PLAYERS}
            similarEntityQueryObject="statsbomb_similar_players"
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

const Player = ({ history }) => {
  const query = new URLSearchParams(useLocation().search);
  const isNewPlayer = query.get('new');
  const playerId = isNewPlayer ? '0' : query.get('player_id');

  return <GoldenPlayerView isNewPlayer={!!isNewPlayer} {...{ playerId, history }} />;
};

export default Player;
